import Konva from "konva";
import BoundingBox from "frontend/geometry/bounding-box";
import { rectIntersectRect } from "frontend/geometry/intersections";
import { intersection } from "rambda";

export type VectorDirections = "up" | "right" | "down" | "left" | "center" | null;

type NonNullVectorDirections = Exclude<VectorDirections, null>;

const NEW_POSITION_VECTORS: Record<
  NonNullVectorDirections,
  {
    direction: NonNullVectorDirections;
    x: number;
    y: number;
  }
> = {
  center: {
    direction: "center",
    x: 0.5,
    y: 0.5,
  },
  right: {
    direction: "right",
    x: 1,
    y: 0,
  },
  down: {
    direction: "down",
    x: 0,
    y: 1,
  },
  left: {
    direction: "left",
    x: -1,
    y: 0,
  },
  up: {
    direction: "up",
    x: 0,
    y: -1,
  },
};

const PADDING_OFFSET = 20;

// Compute the bounding box that contains all nodes, expanding on all sides.
const getContainingBox = (nodes: Konva.Node[]) => {
  return BoundingBox.expandOnAll(
    nodes.map((node) => {
      return node.getClientRect({
        relativeTo: node.getStage() as any,
        skipShadow: true,
      });
    })
  );
};

export const findNewPosition = ({
  previousDirection,
  selectedNodes,
  allNodes,
}: {
  previousDirection: VectorDirections;
  selectedNodes: Konva.Node[];
  allNodes: Konva.Node[];
}): {
  direction: VectorDirections;
  x: number;
  y: number;
} => {
  const bbox = getContainingBox(selectedNodes);

  const allElementsPositions = allNodes.map((node) =>
    node.getClientRect({
      relativeTo: node.getStage() as any,
      skipShadow: true,
    })
  );
  const bboxCenter = {
    x: bbox.width / 2,
    y: bbox.height / 2,
  };

  const newPositionVectors = Object.entries(NEW_POSITION_VECTORS)
    .map(([direction, positionDiff]) => {
      // Calculate new possible position based on the direction vectors.
      const newBbox = BoundingBox.fromRect({
        width: bbox.width,
        height: bbox.height,
        x: bbox.x + positionDiff.x * bbox.width + positionDiff.x * PADDING_OFFSET,
        y: bbox.y + positionDiff.y * bbox.height + positionDiff.y * PADDING_OFFSET,
      });

      // Check if the new position intersects with any existing element's bounding box.
      const isVectorValid = !allElementsPositions.some((nodeBox) => {
        return rectIntersectRect(newBbox, {
          height: nodeBox.height,
          width: nodeBox.width,
          x: nodeBox.x,
          y: nodeBox.y,
        });
      });

      return {
        direction: direction as NonNullVectorDirections,
        isVectorValid,
      };
    })
    .filter((vector) => vector.isVectorValid);

  let direction: NonNullVectorDirections = "center";

  // if all possible start with right
  if (newPositionVectors.length === 4) {
    direction = "right";

    // if right is not possible and previous was left keep going
  } else if (
    intersection(
      newPositionVectors.map((v) => v.direction),
      ["left", "down", "up"]
    ).length === 3 &&
    previousDirection === "left"
  ) {
    direction = "left";

    // any other option
  } else {
    direction = newPositionVectors[0]?.direction ?? "center";
  }

  const positionDiff = NEW_POSITION_VECTORS[direction];

  if (direction === "center") {
    return {
      x: bbox.x + bbox.width,
      y: bbox.y + bbox.height,
      direction: direction,
    };
  }

  // Return the new position adjusted by the direction vector and the top-left offset.
  return {
    x: bbox.x + positionDiff.x * bbox.width + positionDiff.x * PADDING_OFFSET + Math.abs(bboxCenter.x),
    y: bbox.y + positionDiff.y * bbox.height + positionDiff.y * PADDING_OFFSET + Math.abs(bboxCenter.y),
    direction: direction,
  };
};
